import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, UserCheck, Minimize2, Phone } from 'lucide-react';
import { useLanguageStore, useAppearanceStore } from '@/store';
import { ChatMessage, TypingIndicator } from '@/components/chat/ChatMessage';
import { ChatInput } from '@/components/chat/ChatInput';
import { useChatLogic } from '@/components/chat/useChatLogic';
import { useTranslation } from '@/i18n/translations';

const WHATSAPP_ICON_PATH = 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z';

const WhatsAppIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d={WHATSAPP_ICON_PATH} /></svg>
);

const TelegramIcon = ({ className = 'w-4 h-4' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="m16.5 7.5-4.5 9-1.5-4-3.5-1.5Z" />
    <path d="m16.5 7.5-5 4" />
  </svg>
);

const COUNTRY_NAMES = {
  en: { ro: 'Romania', be: 'Belgium', ie: 'Ireland' },
  ru: { ro: 'Румыния', be: 'Бельгия', ie: 'Ирландия' },
  nl: { ro: 'Roemenië', be: 'België', ie: 'Ierland' },
  ro: { ro: 'România', be: 'Belgia', ie: 'Irlanda' }
};

const AIChatWidget = () => {
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  const l = t.chat || {};

  const appearanceConfig = useAppearanceStore((s) => s.config || {});

  const chat = useChatLogic({ language, labels: l });
  const {
    isOpen, messages, inputValue, isLoading, isHumanTakeover,
    customerName, contactInfo, nameConfirmed, showProactive,
    messagesEndRef, inputRef,
    setIsOpen, setInputValue, setCustomerName, setContactInfo, setShowProactive,
    confirmName, sendMessage, requestHuman,
  } = chat;

  const [activeDropdown, setActiveDropdown] = useState(null);

  useEffect(() => {
    const handler = () => {
      setShowProactive(false);
      setIsOpen(true);
    };
    window.addEventListener('openChatWidget', handler);
    return () => window.removeEventListener('openChatWidget', handler);
  }, [setIsOpen, setShowProactive]);

  const getAvailableCountries = (type) => {
    const list = [];
    if (type === 'phone') {
      if (appearanceConfig.chat_phone_ro) list.push({ code: 'ro', link: `tel:${appearanceConfig.chat_phone_ro}` });
      if (appearanceConfig.chat_phone_be) list.push({ code: 'be', link: `tel:${appearanceConfig.chat_phone_be}` });
      if (appearanceConfig.chat_phone_ie) list.push({ code: 'ie', link: `tel:${appearanceConfig.chat_phone_ie}` });
    }
    if (type === 'whatsapp') {
      if (appearanceConfig.chat_whatsapp_ro) list.push({ code: 'ro', link: `https://wa.me/${appearanceConfig.chat_whatsapp_ro.replace(/\+/g, '')}` });
      if (appearanceConfig.chat_whatsapp_be) list.push({ code: 'be', link: `https://wa.me/${appearanceConfig.chat_whatsapp_be.replace(/\+/g, '')}` });
      if (appearanceConfig.chat_whatsapp_ie) list.push({ code: 'ie', link: `https://wa.me/${appearanceConfig.chat_whatsapp_ie.replace(/\+/g, '')}` });
    }
    if (type === 'telegram') {
      if (appearanceConfig.chat_telegram_ro) list.push({ code: 'ro', link: `https://t.me/${appearanceConfig.chat_telegram_ro.replace(/@/g, '')}` });
      if (appearanceConfig.chat_telegram_be) list.push({ code: 'be', link: `https://t.me/${appearanceConfig.chat_telegram_be.replace(/@/g, '')}` });
      if (appearanceConfig.chat_telegram_ie) list.push({ code: 'ie', link: `https://t.me/${appearanceConfig.chat_telegram_ie.replace(/@/g, '')}` });
    }
    return list;
  };

  const handleChannelClick = (type) => {
    const countries = getAvailableCountries(type);
    if (countries.length === 0) return;
    
    if (countries.length === 1) {
      if (countries[0].link.startsWith('tel:')) {
        window.location.href = countries[0].link;
      } else {
        window.open(countries[0].link, '_blank');
      }
      setActiveDropdown(null);
    } else {
      setActiveDropdown(activeDropdown === type ? null : type);
    }
  };

  return (
    <>
      <AnimatePresence>
        {showProactive && !isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 12, x: 8 }}
            animate={{ opacity: 1, y: 0, x: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-6 z-[60] max-w-[280px] cursor-pointer"
            onClick={() => { setShowProactive(false); setIsOpen(true); }}
            data-testid="chat-proactive-bubble"
          >
            <div className="relative bg-background border border-border shadow-xl px-5 py-4">
              <button
                onClick={(e) => { e.stopPropagation(); setShowProactive(false); }}
                className="absolute top-2 right-2 w-5 h-5 flex items-center justify-center text-muted-foreground hover:text-foreground text-xs"
                data-testid="chat-proactive-dismiss"
                aria-label="Dismiss"
              >
                ×
              </button>
              <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground mb-2">
                {l.title || 'FEDYK Furniture'}
              </p>
              <p className="text-sm font-light leading-relaxed pr-2">
                {l.proactive || 'Looking for inspiration? Our architect can prepare a concept for your space.'}
              </p>
            </div>
            <div className="absolute -bottom-1.5 right-8 w-3 h-3 bg-background border-r border-b border-border rotate-45" />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            onClick={() => { setIsOpen(true); setShowProactive(false); }}
            className="fixed bottom-6 right-6 z-[60] w-14 h-14 bg-foreground text-background rounded-full shadow-xl flex items-center justify-center hover:scale-105 transition-transform"
            data-testid="chat-toggle-btn"
          >
            <MessageCircle className="w-6 h-6" />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 right-6 z-[60] w-[400px] max-w-[calc(100vw-32px)] bg-background border border-border shadow-2xl flex flex-col overflow-hidden"
            style={{ height: '560px' }}
            data-testid="chat-window"
          >
            <div className="bg-foreground text-background p-4 flex items-center justify-between shrink-0">
              <div>
                <h4 className="font-medium text-sm">{l.title || 'FEDYK Furniture'}</h4>
                <p className="text-xs text-background/60">{l.subtitle || 'Ask us anything'}</p>
              </div>
              <div className="flex items-center gap-0.5">
                {getAvailableCountries('phone').length > 0 && (
                  <button onClick={() => handleChannelClick('phone')} className="p-1.5 hover:bg-background/10 rounded transition-colors" title={l.call}>
                    <Phone className="w-4 h-4" />
                  </button>
                )}
                {getAvailableCountries('whatsapp').length > 0 && (
                  <button onClick={() => handleChannelClick('whatsapp')} className="p-1.5 hover:bg-background/10 rounded transition-colors" title={l.whatsapp}>
                    <WhatsAppIcon />
                  </button>
                )}
                {getAvailableCountries('telegram').length > 0 && (
                  <button onClick={() => handleChannelClick('telegram')} className="p-1.5 hover:bg-background/10 rounded transition-colors" title="Telegram">
                    <TelegramIcon className="w-4 h-4" />
                  </button>
                )}
                {!isHumanTakeover && nameConfirmed && (
                  <button onClick={requestHuman} className="p-1.5 hover:bg-background/10 rounded transition-colors" title={l.human} data-testid="request-human-btn">
                    <UserCheck className="w-4 h-4" />
                  </button>
                )}
                <button onClick={() => { setIsOpen(false); setActiveDropdown(null); }} className="p-1.5 hover:bg-background/10 rounded transition-colors" data-testid="chat-close-btn">
                  <Minimize2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <AnimatePresence>
              {activeDropdown && (
                <motion.div 
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="bg-neutral-100 border-b border-border px-3.5 py-3 flex items-center justify-between shrink-0 text-xs text-[#0A0A0A] gap-2"
                >
                  <span className="font-medium tracking-wide whitespace-nowrap shrink-0 pl-0.5">
                    {activeDropdown === 'phone' && (l.call || 'Позвонить нам:')}
                    {activeDropdown === 'whatsapp' && (l.whatsapp || 'WhatsApp:')}
                    {activeDropdown === 'telegram' && 'Telegram:'}
                  </span>
                  
                  <div className="flex-1 flex items-center gap-1.5 justify-center">
                    {getAvailableCountries(activeDropdown).map((country) => (
                      <button
                        key={country.code}
                        onClick={() => {
                          if (country.link.startsWith('tel:')) {
                            window.location.href = country.link;
                          } else {
                            window.open(country.link, '_blank');
                          }
                          setActiveDropdown(null);
                        }}
                        className="px-2 py-1 bg-white border border-border hover:border-foreground transition-colors font-medium uppercase text-[9.5px] text-center shrink-0"
                      >
                        {COUNTRY_NAMES[language]?.[country.code] || country.code.toUpperCase()}
                      </button>
                    ))}
                  </div>
                  
                  <button onClick={() => setActiveDropdown(null)} className="text-muted-foreground hover:text-foreground text-sm font-light shrink-0 pr-0.5">
                    ×
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {!nameConfirmed ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8 gap-4">
                <MessageCircle className="w-12 h-12 text-muted-foreground" strokeWidth={1.5} />
                <p className="text-sm text-muted-foreground text-center">{l.nameAsk || 'What is your name?'}</p>
                
                <div className="w-full space-y-2">
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && customerName.trim()) confirmName(); }}
                    placeholder={l.namePlaceholder || 'Your name'}
                    className="w-full px-4 py-3 border border-border text-sm focus:outline-none focus:border-foreground/40 transition-colors text-center"
                    autoFocus
                    data-testid="chat-name-input"
                  />
                  <input
                    type="text"
                    value={contactInfo}
                    onChange={(e) => setContactInfo(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && customerName.trim()) confirmName(); }}
                    placeholder={l.contactPlaceholder || 'Phone or Email (optional)'}
                    className="w-full px-4 py-3 border border-border text-sm focus:outline-none focus:border-foreground/40 transition-colors text-center"
                    data-testid="chat-contact-input"
                  />
                </div>

                <button
                  onClick={confirmName}
                  disabled={!customerName.trim()}
                  className="px-8 py-2.5 bg-foreground text-background text-sm disabled:opacity-40 hover:opacity-90 transition-opacity"
                  data-testid="chat-window"
                >
                  {l.start || 'Start Chat'}
                </button>
                
                <div className="flex gap-4 mt-2 flex-wrap justify-center">
                  {getAvailableCountries('phone').length > 0 && (
                    <button onClick={() => handleChannelClick('phone')} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                      <Phone className="w-3 h-3" /> {l.call || 'Call us'}
                    </button>
                  )}
                  {getAvailableCountries('whatsapp').length > 0 && (
                    <button onClick={() => handleChannelClick('whatsapp')} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                      <WhatsAppIcon className="w-3 h-3" /> {l.whatsapp || 'WhatsApp'}
                    </button>
                  )}
                  {getAvailableCountries('telegram').length > 0 && (
                    <button onClick={() => handleChannelClick('telegram')} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                      <TelegramIcon className="w-3 h-3" /> Telegram
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
                  {messages.map((msg, index) => (
                    <ChatMessage key={msg.id || `msg-${index}`} message={msg} />
                  ))}
                  {isLoading && <TypingIndicator />}
                  <div ref={messagesEndRef} />
                </div>

                {isHumanTakeover && (
                  <div className="px-4 py-2 bg-secondary border-t border-border text-xs text-center text-muted-foreground" data-testid="human-takeover-banner">
                    {l.humanRequested || 'A manager will join shortly...'}
                  </div>
                )}

                <ChatInput
                  ref={inputRef}
                  value={inputValue}
                  onChange={setInputValue}
                  onSend={sendMessage}
                  disabled={isLoading}
                  placeholder={l.placeholder || 'Type a message...'}
                />
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIChatWidget;
